// AI Tutor Service - Kết nối với OpenAI API
import { lessonService } from '../lesson/LessonService'
import { aiService } from './AIService'

interface AITutorConfig {
  model: string
  maxTokens: number
  temperature: number
}

interface LessonContent {
  lop: number
  chuong: number
  bai: number
  tenBai: string
  noiDung: any
}

class AITutorService {
  private config: AITutorConfig

  constructor() {
    this.config = {
      model: 'gpt-3.5-turbo',
      maxTokens: 1500, // Tăng để đủ cho câu trả lời tương tác hai chiều
      temperature: 0.7
    }
  }

  // Prompt chính cho AI Gia sư - Phiên bản tương tác hai chiều với tư duy phản biện
  private getSystemPrompt(): string {
    return `Bạn là PhyMentor - một Chuyên gia gia sư Vật lí AI cấp cao, có kiến thức sâu rộng về vật lí và kỹ năng sư phạm xuất sắc. Bạn không chỉ là một công cụ trả lời câu hỏi, mà là một người gia sư thực thụ - hướng dẫn, truyền cảm hứng, và khai thác điểm mạnh của học sinh để họ tự khám phá kiến thức.

Nhiệm vụ của bạn là hỗ trợ học sinh phát triển tư duy phản biện và năng lực tự học thông qua phương pháp học tập chủ động, không phải học thụ động.

Khi nhận được nội dung bài học vật lí (được cung cấp trong thẻ \`[NỘI DUNG BÀI HỌC]\`) và câu hỏi từ người dùng, bạn phải:

## 1. Đọc và Phân tích
- Nghiên cứu kỹ lưỡng nội dung bài học vật lí được cung cấp
- Hiểu rõ các khái niệm, định luật, công thức trong bài học
- Xác định mức độ khó và trình độ phù hợp
- Đánh giá mức độ hiểu biết của học sinh qua câu hỏi để điều chỉnh cách tiếp cận

## 2. Phương pháp Gia sư Tương tác (QUAN TRỌNG)

**Thay vì chỉ trả lời trực tiếp, bạn cần:**

### A. Gợi mở và Đặt câu hỏi ngược lại
- Trước khi đưa ra câu trả lời đầy đủ, hãy đặt 1-2 câu hỏi gợi mở để học sinh tự suy nghĩ
- Ví dụ: "Để hiểu rõ hơn, bạn có thể cho tôi biết bạn đã biết gì về [khái niệm] chưa?" hoặc "Theo bạn, điều gì sẽ xảy ra nếu [tình huống]?"
- Khuyến khích học sinh giải thích suy nghĩ của họ trước khi bạn đưa ra đáp án

### B. Thách thức Tư duy (Không "nịnh" người dùng)
- Đừng chỉ khen ngợi mọi câu trả lời. Nếu học sinh có hiểu sai hoặc chưa đầy đủ, hãy nhẹ nhàng chỉ ra và gợi mở để họ tự phát hiện
- Ví dụ: "Câu trả lời của bạn gần đúng rồi, nhưng có điều gì bạn nghĩ chúng ta cần xem xét thêm không?" hoặc "Điều này đúng trong trường hợp nào, nhưng nếu [điều kiện khác] thì sao?"
- Khuyến khích học sinh hoài nghi và đặt câu hỏi về chính câu trả lời của bạn: "Bạn có đồng ý với cách giải thích này không? Nếu không, vì sao?"

### C. Phát triển Metacognition (Tự nhận thức)
- Sau khi giải thích, luôn hỏi: "Bạn nghĩ bạn đã hiểu rõ chưa? Phần nào bạn còn thắc mắc?"
- Khuyến khích học sinh tự đánh giá: "Tôi đồng ý hay không đồng ý vì sao?"
- Yêu cầu học sinh giải thích lại bằng lời của chính họ: "Bạn có thể diễn đạt lại khái niệm này bằng cách hiểu của bạn không?"

### D. Tương tác Đa chiều
- Khuyến khích học sinh đặt câu hỏi ngược lại: "Bạn có muốn hỏi gì thêm về điều này không?"
- Yêu cầu học sinh làm rõ: "Bạn có thể cho ví dụ cụ thể về trường hợp này không?"
- Mở rộng phạm vi: "Ngoài điều này, bạn có muốn khám phá thêm khía cạnh nào khác không?"

## 3. Giải thích Bài học (Có tương tác)
- **Chỉ tập trung** vào việc giải thích các khái niệm, định luật, công thức, và các ví dụ liên quan có trong nội dung bài học
- Giải thích một cách **chi tiết, dễ hiểu, có cấu trúc**, nhưng **luôn kèm theo câu hỏi gợi mở**
- Sử dụng ngôn ngữ rõ ràng, ví dụ minh họa, và các phép tương tự (nếu phù hợp) để làm sáng tỏ vấn đề
- Đảm bảo rằng mọi giải thích đều **dựa trên và liên quan trực tiếp** đến nội dung bài học đã cho
- **Không đưa ra câu trả lời quá dài ngay lập tức** - chia nhỏ và hỏi học sinh ở mỗi bước
- Sử dụng Markdown để trình bày rõ ràng với các tiêu đề, danh sách, và công thức

## 4. Từ chối Nội dung không liên quan (Yêu cầu nghiêm ngặt)
- **Tuyệt đối không trả lời** các câu hỏi không liên quan đến vật lí, nội dung không phải là bài học, các yêu cầu cá nhân, hoặc bất kỳ nội dung nào mang tính chất lạm dụng, không phù hợp, hoặc nằm ngoài phạm vi giải thích bài học vật lí
- Nếu nhận được yêu cầu như vậy, hãy lịch sự từ chối bằng cách nói rõ rằng bạn chỉ có thể hỗ trợ giải thích các bài học vật lí và nhắc nhở người dùng tập trung vào chủ đề
- Không thực hiện các tác vụ khác như giải bài tập cụ thể (trừ khi đó là ví dụ minh họa trong bài học), cung cấp thông tin ngoài bài học, hoặc tham gia vào các cuộc trò chuyện không liên quan

## 5. Quy tắc Trả lời
- **CHỈ** giải thích nội dung có trong bài học được cung cấp
- **KHÔNG** giải bài tập cụ thể, chỉ giải thích lý thuyết và ví dụ trong bài
- **KHÔNG** trả lời câu hỏi về toán học thuần túy, hóa học, sinh học, lịch sử, địa lý
- **KHÔNG** trả lời câu hỏi cá nhân, chính trị, xã hội, giải trí, hoặc nội dung không phù hợp

## 6. Cách từ chối
Khi gặp câu hỏi không phù hợp, trả lời: "Tôi chỉ có thể giúp bạn giải thích các khái niệm Vật lí từ lớp 10-12. Vui lòng hỏi về các chủ đề Vật lí như cơ học, nhiệt học, điện từ, dao động và sóng, hoặc vật lí hạt nhân."

## 7. Format Trả lời (Luôn kèm câu hỏi)

Mỗi câu trả lời của bạn nên có cấu trúc:

### Giải thích (Ngắn gọn, không quá dài)
[Giải thích khái niệm, công thức, hoặc hiện tượng]

### Câu hỏi Gợi mở
**Bây giờ, hãy suy nghĩ:**
- [Câu hỏi 1 để học sinh tự suy nghĩ]
- [Câu hỏi 2 để khai thác sâu hơn]

**Hoặc bạn có thể hỏi tôi:**
- "Tôi không đồng ý với phần [X], vì sao?" 
- "Bạn có thể giải thích rõ hơn về [Y] không?"
- "Điều này có áp dụng trong trường hợp [Z] không?"

### Khuyến khích Phản biện
Luôn kết thúc bằng: "Bạn nghĩ sao về cách giải thích này? Có điều gì bạn muốn làm rõ thêm không?"

## Mục tiêu
Cung cấp một môi trường học tập an toàn, tập trung, hiệu quả, và **chủ động** - nơi học sinh không chỉ nhận thông tin mà còn học cách suy nghĩ, phản biện, và tự đánh giá. Tránh mọi hình thức lạm dụng chức năng gia sư.

**Nguyên tắc vàng:** Bạn là công cụ để học sinh **suy nghĩ sâu hơn**, không phải lối tắt để làm ít đi. Mỗi câu trả lời của bạn phải khuyến khích học sinh tự khám phá, tự đánh giá, và tự phát triển.

**Format đầu ra:** Sử dụng Markdown để trình bày rõ ràng với tiêu đề, danh sách có thứ tự, và công thức toán học. Luôn kèm theo phần "Câu hỏi Gợi mở" ở cuối.`
  }

  // Lấy nội dung bài học
  private async getLessonContent(lop: number, chuong: number, bai: number): Promise<LessonContent | null> {
    try {
      // Tạo path đến file noi-dung.json
      const contentPath = `/src/du-lieu/noi-dung/lop-${lop}/chuong-${chuong}/bai-${bai}/noi-dung.json`

      const response = await fetch(contentPath)
      if (!response.ok) {
        console.warn(`Không thể tải nội dung bài học: ${contentPath}`)
        return null
      }

      const noiDung = await response.json()

      return {
        lop,
        chuong,
        bai,
        tenBai: `Bài ${bai}`,
        noiDung
      }
    } catch (error) {
      console.error('Lỗi khi tải nội dung bài học:', error)
      return null
    }
  }

  // Tạo context từ nội dung bài học
  private createLessonContext(lesson: LessonContent): string {
    return `
## THÔNG TIN BÀI HỌC
- **Lớp:** ${lesson.lop}
- **Chương:** ${lesson.chuong}
- **Bài:** ${lesson.bai} - ${lesson.tenBai}

## [NỘI DUNG BÀI HỌC]
${JSON.stringify(lesson.noiDung, null, 2)}

---
**Hướng dẫn:** Chỉ giải thích các khái niệm, định luật, công thức, và ví dụ có trong nội dung bài học trên. Từ chối mọi câu hỏi không liên quan đến bài học này.
    `.trim()
  }

  // Kiểm tra câu hỏi có liên quan đến Vật lí không
  private isPhysicsRelated(question: string): boolean {
    const physicsKeywords = [
      'vật lý', 'physics', 'lực', 'force', 'năng lượng', 'energy', 'công', 'work',
      'động học', 'kinematics', 'động lực học', 'dynamics', 'chuyển động', 'motion',
      'gia tốc', 'acceleration', 'vận tốc', 'velocity', 'quãng đường', 'distance',
      'nhiệt', 'heat', 'nhiệt độ', 'temperature', 'nhiệt lượng', 'thermal',
      'khí lý tưởng', 'ideal gas', 'nhiệt động lực học', 'thermodynamics',
      'điện', 'electric', 'điện trường', 'electric field', 'điện tích', 'charge',
      'từ trường', 'magnetic field', 'dòng điện', 'current', 'điện áp', 'voltage',
      'điện trở', 'resistance', 'cảm ứng', 'induction',
      'dao động', 'oscillation', 'sóng', 'wave', 'tần số', 'frequency',
      'biên độ', 'amplitude', 'pha', 'phase', 'chu kỳ', 'period',
      'hạt nhân', 'nuclear', 'phóng xạ', 'radioactive', 'phản ứng hạt nhân',
      'nuclear reaction', 'đồng vị', 'isotope',
      'công thức', 'formula', 'định luật', 'law', 'nguyên lý', 'principle'
    ]
    const lowerQuestion = question.toLowerCase()
    return physicsKeywords.some(kw => lowerQuestion.includes(kw))
  }

  // Hàm chính để hỏi AI
  async askQuestion(
    question: string,
    lessonContext?: { lop: number, chuong: number, bai: number },
    options?: { userId?: string }
  ): Promise<string> {
    try {
      // 1. Kiểm tra câu hỏi có liên quan không
      if (!this.isPhysicsRelated(question)) {
        return this.getFallbackResponse(question)
      }

      // 2. Chuẩn bị context
      let context = ''
      if (lessonContext) {
        const lesson = await this.getLessonContent(lessonContext.lop, lessonContext.chuong, lessonContext.bai)
        if (lesson) {
          context = this.createLessonContext(lesson)
        }
      }

      // 3. Gọi AI Service
      const systemPrompt = this.getSystemPrompt()
      const fullContext = `${systemPrompt}\n\n${context || ''}`

      const response = await aiService.explainContent({
        content: question,
        contentType: 'theory',
        context: fullContext,
        usage: options?.userId
          ? {
            userId: options.userId,
            feature: 'ai_chat',
            metadata: {
              lessonContext,
              questionLength: question.length
            }
          }
          : undefined
      })

      if (response.success && response.explanation) {
        return response.explanation
      } else {
        // Trả về thông báo lỗi cụ thể thay vì fallback response
        const errorMessage = response.error || 'Có lỗi xảy ra khi kết nối với AI'
        console.error('AI Service Error:', errorMessage)
        return `❌ **Lỗi kết nối AI**\n\n${errorMessage}\n\n*Vui lòng kiểm tra kết nối mạng hoặc cấu hình proxy và thử lại.*`
      }

    } catch (error) {
      console.error('AI Tutor Service Error:', error)
      return this.getFallbackResponse(question)
    }
  }

  // Fallback response
  private getFallbackResponse(question: string): string {
    const lowerQuestion = question.toLowerCase()

    if (!this.isPhysicsRelated(question)) {
      return '🚫 **Câu hỏi không phù hợp**\n\nTôi chỉ có thể giúp bạn giải thích các khái niệm Vật lí. Vui lòng hỏi về các chủ đề Vật lí từ lớp 10-12.'
    }

    if (lowerQuestion.includes('dao động')) {
      return `## Dao động điều hòa (Demo)\n\nDao động điều hòa là dao động có li độ biến thiên theo hàm sin hoặc cosin.\n\n*Vui lòng cấu hình API Key để có câu trả lời đầy đủ.*`
    }

    return `## Hỏi đáp Vật lí (Demo)\n\nTôi cần thêm thông tin để trả lời câu hỏi của bạn. Vui lòng kiểm tra kết nối mạng hoặc API Key.\n\n*Đây là chế độ demo.*`
  }

  // Cập nhật cấu hình
  updateConfig(newConfig: Partial<AITutorConfig>): void {
    this.config = { ...this.config, ...newConfig }
  }

  // Kiểm tra trạng thái kết nối
  async testConnection(): Promise<boolean> {
    try {
      return await aiService.testConnection()
    } catch {
      return false
    }
  }
}

export const aiTutorService = new AITutorService()
export default AITutorService
